import * as THREE from "three";
import { U } from "../streetScene.js";
import { CharBillboard } from "../charBillboard.js";
import { DEFAULT_APPEARANCE, type CharacterAppearance, type CityNpc } from "@ghostland/shared";

/**
 * Renders the server-authoritative CITY NPCs (walk-in restaurant customers; later wandering townsfolk)
 * as humanoid `CharBillboard` paperdolls driven by the synced `CityNpc` schema — position + heading come
 * from the server, so every player sees the same NPC in the same spot. Mirrors the GuardController shape
 * (add/sync/tick with LERP smoothing) but carries no combat/health. Interiors are flat (y = 0).
 */
const LERP = 0.25;

/** A deterministic everyman look from a seed, so every client renders the same NPC. Uses an xorshift32
 *  PRNG (exact 32-bit ops — no float precision loss) so every field actually varies. Recolourable
 *  tops/bottoms only (styles 1-4 / 1-3) so any colour index is valid. */
export function appearanceFromSeed(seed: number): CharacterAppearance {
  let s = (seed | 0) || 0x9e3779b9;
  const r = (n: number): number => {
    s ^= s << 13; s |= 0;
    s ^= s >>> 17;
    s ^= s << 5; s |= 0;
    return (s >>> 0) % n;
  };
  // Tops: mostly recolourable basics (tee / circle / tech), sometimes a pre-coloured flavour piece
  // (ghost tee / bomber / suit) — those carry their own colours, so they MUST stay colour 0.
  const flavorTop = r(10) < 3; // ~30%
  const top = flavorTop ? [2, 9, 6][r(3)] : [1, 3, 4][r(3)];
  const topColor = flavorTop ? 0 : r(9); // 9 garment colourways incl. Black(1), Blue(2), Green(5), Red(6)…
  // Bottoms: jeans / tech joggers recolour freely (so black pants etc. appear); workpants are pre-coloured.
  const workpants = r(5) === 0; // ~20%
  const bottom = workpants ? 2 : (r(2) === 0 ? 1 : 3);
  const bottomColor = workpants ? 0 : r(9);
  return {
    ...DEFAULT_APPEARANCE,
    face: r(2), skinTone: r(6),
    hair: r(5), hairColor: r(10), // 0 = bald (occasional); 10 hair colourways
    top, topColor,
    bottom, bottomColor,
    shoes: 1, shoesColor: r(9),
    hat: r(4) === 0 ? 1 : 0, hatColor: r(9), // ~1/4 wear a cap
  };
}

interface NpcEntity {
  bb: CharBillboard;
  x: number; z: number;             // interpolated render position (world units)
  serverX: number; serverZ: number; // authoritative target (world units)
  heading: number; moving: boolean;
  name: string;
}

export class CityNpcController {
  private readonly npcs = new Map<string, NpcEntity>();
  private visible = true;

  constructor(private readonly scene: THREE.Scene) {}

  add(key: string, n: CityNpc): void {
    if (this.npcs.has(key)) { this.sync(key, n); return; }
    const bb = new CharBillboard({ combatless: true }); // townsfolk/customers never fight → cheap, shared-atlas build
    void bb.setAppearance(appearanceFromSeed(n.look));
    bb.group.visible = this.visible;
    bb.setPosition(n.x * U, 0, n.y * U);
    bb.fadeIn(300);
    this.scene.add(bb.group);
    this.npcs.set(key, { bb, x: n.x * U, z: n.y * U, serverX: n.x * U, serverZ: n.y * U, heading: n.heading, moving: n.moving, name: n.name });
  }

  sync(key: string, n: CityNpc): void {
    const e = this.npcs.get(key); if (!e) return;
    e.serverX = n.x * U; e.serverZ = n.y * U; e.heading = n.heading; e.moving = n.moving;
    if (n.name) e.name = n.name;
  }

  /** This NPC's display name (for the target frame / talk dialog), or a generic fallback. */
  nameOf(key: string): string {
    return this.npcs.get(key)?.name || "Townsperson";
  }

  /** Pixel-accurate pick of a VISIBLE city-NPC billboard under the ray (click-to-select). */
  pick(ray: THREE.Raycaster): string | null {
    const live = [...this.npcs.entries()].filter(([, e]) => e.bb.group.visible);
    const hits = ray.intersectObjects(live.map(([, e]) => e.bb.group), true);
    for (const h of hits) {
      if (!h.uv) continue;
      const ent = live.find(([, e]) => e.bb.group === h.object.parent);
      if (ent && ent[1].bb.hitTestUV(h.uv)) return ent[0];
    }
    return null;
  }
  /** Visible city NPCs within `range` world units of (wx,wz), nearest first — for Tab targeting. */
  inRange(wx: number, wz: number, range: number): { key: string; x: number; z: number }[] {
    const r2 = range * range, out: { key: string; x: number; z: number }[] = [];
    for (const [key, e] of this.npcs) {
      if (!e.bb.group.visible) continue;
      const dx = e.x - wx, dz = e.z - wz;
      if (dx * dx + dz * dz <= r2) out.push({ key, x: e.x, z: e.z });
    }
    out.sort((a, b) => (a.x - wx) ** 2 + (a.z - wz) ** 2 - ((b.x - wx) ** 2 + (b.z - wz) ** 2));
    return out;
  }
  /** Live render position (world units) of a city NPC, or null — for the ground selection ring. */
  posOf(key: string): { x: number; z: number } | null {
    const e = this.npcs.get(key);
    return e ? { x: e.x, z: e.z } : null;
  }
  has(key: string): boolean { return this.npcs.has(key); }

  remove(key: string): void {
    const e = this.npcs.get(key); if (!e) return;
    this.scene.remove(e.bb.group); e.bb.dispose();
    this.npcs.delete(key);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    for (const e of this.npcs.values()) e.bb.group.visible = v;
  }

  /** Temporarily show the NPCs standing in `zone` for an off-screen render (window views peeking
   *  into a zone the local player isn't in). Returns the restore fn. `zoneOf` takes server px. */
  revealForSnapshot(zone: string, zoneOf: (x: number, y: number) => string): () => void {
    const undo: (() => void)[] = [];
    for (const e of this.npcs.values()) {
      if (e.bb.group.visible) continue;
      if (zoneOf(e.serverX / U, e.serverZ / U) !== zone) continue;
      e.bb.group.visible = true;
      undo.push(() => { e.bb.group.visible = false; });
    }
    return () => { for (const u of undo) u(); };
  }

  /** Render only NPCs whose interior zone matches the player's current zone (so a Watabaga customer
   *  isn't drawn while you're inside Ghost's). `zoneOf` takes server px. */
  tick(dt: number, camYaw: number, currentZone: string, zoneOf: (x: number, y: number) => string): void {
    const dtMs = dt * 1000;
    for (const e of this.npcs.values()) {
      const show = zoneOf(e.serverX / U, e.serverZ / U) === currentZone; // U = world units / px
      e.bb.group.visible = show;
      if (!show) continue;
      const dx = e.serverX - e.x, dz = e.serverZ - e.z;
      if (dx * dx + dz * dz > 36) { e.x = e.serverX; e.z = e.serverZ; } // door teleport (big jump) → snap, don't streak
      else { e.x += dx * LERP; e.z += dz * LERP; }
      e.bb.setPosition(e.x, 0, e.z);
      e.bb.setHeading(e.heading, e.moving, false);
      e.bb.tick(dtMs, camYaw);
    }
  }

  dispose(): void {
    for (const key of [...this.npcs.keys()]) this.remove(key);
  }
}
